import React from 'react';
import { Box } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { GanttEntry } from './ganttTypes';
import { TOKENS, fmtDims } from './ganttConfig';
import { fmtClock } from './GanttBar';
import { contrastText } from './barStyle';

export interface HoverState {
  entry: GanttEntry;
  x: number;
  y: number;
}

interface Props {
  hover: HoverState | null;
}

const fmtDur = (min: number) => {
  const total = Math.round(min); // planned_total is a float (ms/60000) — round to whole minutes
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
};
const fmtQty = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : String(n));
const dueLabel = (d: number | null) =>
  d == null ? 'Due —' : d === 0 ? 'Due today' : d === 1 ? 'Due tomorrow' : d < 0 ? `${-d}d overdue` : `Due +${d}d`;

const Cell: React.FC<{ l: string; children: React.ReactNode; tone?: string }> = ({ l, children, tone }) => (
  <Box>
    <Box sx={{ fontSize: 10, color: TOKENS.ink3, mb: 0.25 }}>{l}</Box>
    <Box sx={{ fontSize: 12.5, fontWeight: 600, color: tone ?? TOKENS.ink, fontFamily: TOKENS.monoFont, fontVariantNumeric: 'tabular-nums' }}>{children}</Box>
  </Box>
);

export default function HoverPopover({ hover }: Props) {
  if (!hover) return null;
  const e = hover.entry;
  const st = e.style;
  const W = 320;
  // Drives the flip-up threshold. Dropped from 358 when the customer name, the
  // Press cell and the unit captions came out (a grid row + two text lines).
  const H = 288;
  let x = hover.x + 18;
  let y = hover.y - 10;
  if (x + W > window.innerWidth) x = hover.x - (W + 18);
  if (y + H > window.innerHeight) y = window.innerHeight - (H + 10);
  if (y < 10) y = 10;

  const inks = e.seps.filter((s) => s.role !== 'coating');
  const coat = e.seps.find((s) => s.role === 'coating');
  const artworkMissing = e.artworkStatus === 'not_found' || e.artworkStatus === 'error';
  // No status yet = the extraction is still queued (or this tile was never
  // resolved at all). Distinct from "resolved with nothing printable".
  const artworkPending = e.artworkStatus === undefined;

  return (
    <Box sx={{
      position: 'fixed', left: x, top: y, width: W, zIndex: 1500,
      background: TOKENS.panel, borderRadius: '14px', boxShadow: TOKENS.shadowMd,
      overflow: 'hidden', pointerEvents: 'none', display: 'flex',
    }}>
      <Box sx={{ width: 5, background: st.fill, flexShrink: 0 }} />
      <Box sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 15, color: TOKENS.ink }}>{e.job}</Box>
          </Box>
          <Box sx={{
            flexShrink: 0, px: 0.75, py: 0.25, borderRadius: '6px', fontSize: 10.5, fontWeight: 700,
            background: st.fill, color: st.text, border: st.border ? `1px solid ${st.border}` : 'none',
          }}>{e.cust}</Box>
        </Box>
        <Box sx={{ fontSize: 12, color: TOKENS.ink, mt: 1, mb: 0.75, lineHeight: 1.3 }}>{e.desc}</Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
          <Box sx={{ fontSize: 10, color: TOKENS.ink3 }}>Item code</Box>
          <Box sx={{ fontFamily: TOKENS.monoFont, fontSize: 12, fontWeight: 700, color: e.ref ? TOKENS.ink : TOKENS.ink3 }}>
            {e.ref || '—'}
          </Box>
        </Box>
        {/* Board substrate — production.jobs.stock_code. Full-width (not a grid
            cell) because stock codes are long, e.g. GC2-356-660X786F. */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
          <Box sx={{ fontSize: 10, color: TOKENS.ink3, flexShrink: 0 }}>Raw material</Box>
          <Box sx={{
            fontFamily: TOKENS.monoFont, fontSize: 12, fontWeight: 700,
            color: e.stockCode ? TOKENS.ink : TOKENS.ink3,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {e.stockCode || '—'}
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
          <Cell l="Forme">{e.forme ?? '—'}</Cell>
          <Cell l="Coating" tone={e.coating && e.coating !== 'NONE' ? TOKENS.ink : TOKENS.ink3}>
            {e.coating ?? '—'}
          </Cell>
          <Cell l="Size (mm)">
            {fmtDims(e.length, e.width) ?? '—'}
          </Cell>
          <Cell l="Run length">{fmtQty(e.sheets)} sh</Cell>
          {e.status === 'in_progress' && e.projEnd != null ? (
            // Running job: show the committed baseline AND the live ETA, so the
            // operator reads plan-vs-reality at a glance (and the projected end
            // matches where the bar now finishes / the next job starts).
            <>
              <Cell l="Planned">{fmtClock(e.planStart)}–{fmtClock(e.planEnd)}</Cell>
              <Cell l="Projected finish" tone={TOKENS.ink}>{fmtClock(e.start)}–{fmtClock(e.projEnd)}</Cell>
            </>
          ) : (
            <>
              <Cell l="Window">{fmtClock(e.start)}–{fmtClock(e.start + e.planned_total)}</Cell>
              <Cell l="Duration">{fmtDur(e.planned_total)}</Cell>
            </>
          )}
          {e.status === 'in_progress' || e.status === 'completed' ? (
            // Completed bars show the SAME good/ordered · % yield as a running
            // job — the final count instead of a live one.
            <Cell l={e.status === 'completed' ? 'Produced' : 'Run progress'} tone={TOKENS.ink}>
              {e.sheetsDone != null && e.sheets
                ? `${e.sheetsDone.toLocaleString()} / ${e.sheets.toLocaleString()} · ${Math.round((e.progress ?? 0) * 100)}%`
                : '—'}
            </Cell>
          ) : (
            <Cell l={dueLabel(e.due)} tone={TOKENS.ink2}>Planned</Cell>
          )}
        </Box>
        {e.status === 'in_progress' && (e.machineStatus || e.machineActivity) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, fontSize: 11.5 }}>
            <Box sx={{
              width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
              background: e.machineStatus === 'Running' ? TOKENS.good : TOKENS.warn,
            }} />
            <Box sx={{ fontWeight: 700, color: TOKENS.ink }}>{e.machineStatus ?? '—'}</Box>
            {e.machineActivity && <Box sx={{ color: TOKENS.ink2 }}>· {e.machineActivity}</Box>}
            {e.liveSpeed != null && e.liveSpeed > 0 && (
              <Box sx={{ color: TOKENS.ink3, fontFamily: TOKENS.monoFont }}>· {e.liveSpeed.toLocaleString()}/hr</Box>
            )}
            {e.operator && <Box sx={{ color: TOKENS.ink3, ml: 'auto', whiteSpace: 'nowrap' }}>{e.operator}</Box>}
          </Box>
        )}
        {artworkMissing ? (
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 0.75, p: 1, borderRadius: '8px',
            background: 'rgba(214,120,20,0.10)', border: `1px solid ${TOKENS.warn}`,
          }}>
            <WarningAmberRoundedIcon sx={{ fontSize: 16, color: TOKENS.warn, mt: '1px', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 11.5, fontWeight: 700, color: TOKENS.ink }}>Artwork not found in Cloudflow</Box>
              <Box sx={{ fontSize: 9.5, color: TOKENS.ink3, mt: 0.25, wordBreak: 'break-all', fontFamily: TOKENS.monoFont }}>
                {e.artworkPath ?? 'no artwork path (missing item code)'}
              </Box>
              <Box sx={{ fontSize: 10, color: TOKENS.ink2, mt: 0.4 }}>Check the item code / Pre-Press file naming.</Box>
            </Box>
          </Box>
        ) : artworkPending ? (
          // Artwork hasn't come back yet. Say that, rather than rendering the
          // sequence with zero inks ("0 ink" reads as a fact about the job) or
          // the old placeholder CMYK badges (which read as four real inks).
          <Box sx={{ fontSize: 10.5, color: TOKENS.ink3, fontStyle: 'italic' }}>
            Reading artwork separations…
          </Box>
        ) : (
          <>
            <Box sx={{ fontSize: 10, color: TOKENS.ink3, mb: 0.5 }}>Separation sequence · {inks.length} ink{coat ? ' + coat' : ''}</Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {/* No U1..Un caption: this list is the artwork's PDF colorant
                  order, NOT a press-unit assignment (that lives in
                  production.ink_sku_separation.print_unit, via Ink Assign, and
                  is unset for most SKUs). Numbering it read as an allocation
                  nobody had made. */}
              {inks.map((s, i) => (
                <Box key={i} sx={{ width: 26, height: 26, borderRadius: '6px', background: s.hex, display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 700, color: contrastText(s.hex) }}>{s.short}</Box>
              ))}
              {coat && (
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: '6px', background: coat.hex, display: 'grid', placeItems: 'center', fontSize: 11, color: '#1a1a1a', border: '1px dashed #9bb6d0' }}>◇</Box>
                  <Box sx={{ fontSize: 9, color: TOKENS.ink3, mt: 0.25 }}>Coat</Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
